/*
  Marie Gems scroll-scrub engine.
  Drives a canvas from a tiled WebP spritesheet, keyed to scroll position within
  a pinned (position: sticky) track. No external dependencies.

  Frame source: real camera footage (not AI-rendered), so frame count and
  spritesheet grid are fixed per clip — see data attributes on each .scrub block.
*/
(function () {
  'use strict';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initScrub(root) {
    var canvas = root.querySelector('.scrub-canvas');
    var track = root; // data-scrub lives on the .scrub-track element itself
    var sticky = root.querySelector('.scrub-sticky');
    if (!canvas || !track || !sticky) return;

    var spriteUrl = root.getAttribute('data-sprite');
    var frameCount = parseInt(root.getAttribute('data-frames'), 10) || 1;
    var cols = parseInt(root.getAttribute('data-cols'), 10) || 1;
    var rows = parseInt(root.getAttribute('data-rows'), 10) || 1;
    var frameW = parseInt(root.getAttribute('data-frame-w'), 10) || 1;
    var frameH = parseInt(root.getAttribute('data-frame-h'), 10) || 1;

    // ---------- caption loop: cycles on its own timer, start to finish,
    // completely independent of scroll position and of the video frame.
    // Scrolling no longer changes which caption is showing. ----------
    var captionEls = Array.prototype.slice.call(root.querySelectorAll('[data-cap-from]'));
    if (captionEls.length) {
      var capIndex = 0;
      var setActiveCaption = function (i) {
        captionEls.forEach(function (el, ei) { el.classList.toggle('is-active', ei === i); });
      };
      setActiveCaption(0);
      setInterval(function () {
        capIndex = (capIndex + 1) % captionEls.length;
        setActiveCaption(capIndex);
      }, 3200);
    }

    if (reduceMotion) {
      root.classList.add('scrub-static');
      return; // poster image (CSS background) + static caption list stands in, see liquid markup
    }

    // data-autoplay sections (e.g. the peel-moment "watch it peel" block)
    // play through their frames on their own, pinned in place — not tied to
    // scroll at all.
    var autoplay = root.hasAttribute('data-autoplay');

    // The sprite sheet is one giant tiled image (~12000x8540px for the
    // hero) — decoding and repeatedly redrawing a bitmap that large is
    // expensive main-thread work on any device, and downloading the full
    // 5MB+ file is wasted bytes on a phone. Below desktop width, skip the
    // canvas frame-scrubber entirely — for BOTH scroll-driven and autoplay
    // blocks (autoplay used to be exempted from this, which is why the
    // hero's 5.2MB sprite and its continuous redraw loop were shipping to
    // every mobile visitor) — but KEEP the sticky pin and the CSS poster
    // background (theme.css's `.scrub-sticky { background-image: var(--poster-url, none), ... }`
    // rule already handles this, with a gradient fallback if the poster
    // is missing), so the section still looks intentional: a static hero
    // photo instead of a scroll-scrubbed video.
    var isDesktop = window.innerWidth > 1100;
    if (!isDesktop) {
      root.classList.add('scrub-canvas-off');
      return;
    }

    // alpha:false skips alpha compositing (every frame is fully opaque, so
    // this is free perf); desynchronized hints the browser it can present
    // frames without blocking on the main thread's paint sync.
    var ctx = canvas.getContext('2d', { alpha: false, desynchronized: true });
    var img = new Image();
    img.decoding = 'async';
    // Deprioritize this fetch relative to whatever else is in flight at the
    // same moment. The sprite is already lazy-loaded (IntersectionObserver
    // below only calls initScrub once the block is ~600px from view), so
    // this just keeps it from contending with more important requests
    // (product images, the Shopify cart-sync chain, etc.) once it starts.
    //
    // TODO(perf, unresolved): the real fix is the asset itself, not loading
    // order. assets/hero-sprite.webp is still 5,198.2 KiB — a 12000x8540px
    // (~102 megapixel) tiled sheet of 250 frames at 480x854 each. That's
    // well past Shopify's 20-megapixel Files/CDN processing cap (confirmed
    // 2026-08-28: fileCreate on this asset fails with INVALID_IMAGE_RESOLUTION),
    // so it can't be auto-resized through Shopify's own image pipeline —
    // it has to be recompressed/resized externally and re-uploaded as a
    // theme asset. Each frame is drawn stretched to cover the full hero
    // width (see drawImage below), i.e. already upscaled well past its
    // 480px source width on any normal desktop viewport, so there is real
    // headroom to shrink the source sheet (fewer source pixels, same or
    // better perceived sharpness) — see the PageSpeed audit for the full
    // writeup and suggested target specs (or re-encode as an actual
    // <video> instead of a canvas-scrubbed sprite, which is the more
    // durable fix).
    if ('fetchPriority' in img) img.fetchPriority = 'low';
    var ready = false;
    var currentFrame = -1;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);

    img.onload = function () {
      ready = true;
      sizeCanvas();
      drawFrame(0);
      if (autoplay) startAutoplay();
    };
    img.src = spriteUrl;

    function startAutoplay() { var dir = 1, f = 0; var last = performance.now(); var visible = true; if ('IntersectionObserver' in window) { var playIo = new IntersectionObserver(function (entries) { entries.forEach(function (entry) { visible = entry.isIntersecting; }); }, { rootMargin: '200px 0px' }); playIo.observe(track); } function tick(now) { if (visible && now - last > 90) { f += dir; if (f >= frameCount - 1) dir = -1; if (f <= 0) dir = 1; drawFrame(f); last = now; } requestAnimationFrame(tick); } requestAnimationFrame(tick); }

    function sizeCanvas() {
      var rect = canvas.getBoundingClientRect();
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
    }

    function drawFrame(index) {
      if (!ready || index === currentFrame) return;
      index = Math.max(0, Math.min(frameCount - 1, index));
      currentFrame = index;
      var col = index % cols;
      var row = Math.floor(index / cols);
      var sx = col * frameW;
      var sy = row * frameH;

      // cover-fit the source frame into the canvas
      var cw = canvas.width, ch = canvas.height;
      var srcRatio = frameW / frameH;
      var dstRatio = cw / ch;
      var dw, dh, dx, dy;
      if (dstRatio > srcRatio) {
        dw = cw; dh = cw / srcRatio; dx = 0; dy = (ch - dh) / 2;
      } else {
        dh = ch; dw = ch * srcRatio; dy = 0; dx = (cw - dw) / 2;
      }
      ctx.clearRect(0, 0, cw, ch);
      ctx.drawImage(img, sx, sy, frameW, frameH, dx, dy, dw, dh);
    }

    if (autoplay) {
      // Plays on its own via startAutoplay() above — no scroll tie at all,
      // just keep the canvas the right pixel size on resize.
      window.addEventListener('resize', function () {
        sizeCanvas();
        currentFrame = -1;
      });
      return;
    }

    // ---------- scroll-driven progress ----------
    // The sprite sheet is a single large tiled image (every frame in one
    // file, ~12000x8540px for the hero), so the canvas only redraws it when
    // the scroll position actually changes — never continuously. An earlier
    // version also redrew it on an idle timer to "drift" the video while
    // the visitor wasn't scrolling; on tablets that continuous redraw of
    // such a large source image was the real cause of felt lag/glitchiness,
    // so it's gone. The frame is static until the visitor scrolls.
    //
    // nearViewport gates the scroll handler: once the track is far above or
    // below the viewport, skip the getBoundingClientRect() read entirely
    // instead of doing it on every single scroll event for the rest of the
    // page.
    var nearViewport = true;
    if ('IntersectionObserver' in window) {
      nearViewport = false;
      var proximityIo = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) { nearViewport = entry.isIntersecting; });
      }, { rootMargin: '200px 0px' });
      proximityIo.observe(track);
    }

    var ticking = false;
    function syncToScroll() {
      if (ticking || !nearViewport) return;
      ticking = true;
      requestAnimationFrame(function () {
        var rect = track.getBoundingClientRect();
        var trackH = track.offsetHeight - sticky.offsetHeight;
        var progress = trackH > 0 ? (-rect.top) / trackH : 0;
        progress = Math.max(0, Math.min(1, progress));
        var idx = Math.round(progress * (frameCount - 1));
        drawFrame(idx);
        ticking = false;
      });
    }
    function onScroll() {
      if (!nearViewport) return;
      syncToScroll();
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () {
      sizeCanvas();
      currentFrame = -1;
      syncToScroll();
    });
    syncToScroll();
  }

  // ---------- lazy-init: only load a sprite (5MB+ each) once its block is
  // actually near the viewport, so below-the-fold blocks (e.g. the peel
  // section) don't compete with the hero and page-load JS for bandwidth ----------
  var blocks = document.querySelectorAll('[data-scrub]');
  if ('IntersectionObserver' in window) {
    var lazyIo = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          initScrub(entry.target);
          lazyIo.unobserve(entry.target);
        }
      });
    }, { rootMargin: '600px 0px' });
    blocks.forEach(function (el) { lazyIo.observe(el); });
  } else {
    blocks.forEach(initScrub);
  }
})();
